import React, { useState } from "react";
import { motion } from "framer-motion"; //  Import Framer Motion
import {
  registerUser,
  resendVerificationEmail,
  isValidEmail,
  normalizePhone,
} from "services/authService";
import { authError, authErrorMessage, reportAuthError } from "services/authErrors";
import useCooldown from "Hooks/useCooldown";
import { Link } from "react-router-dom";
import AuthHeader from "Pages/Auth/components/AuthHeader";

const RESEND_DELAY = 60; // seconds between two confirmation emails
const MIN_PASSWORD = 6; // Firebase's minimum

const inputClass = (invalid) =>
  `w-full p-2 border rounded-lg bg-white text-black light-input focus:outline-none focus:ring-2 ${
    invalid ? "border-red-500 ring-red-500" : "focus:ring-blue-400"
  }`;

const Signup = () => {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [phone, setPhone] = useState("");
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  // Set once the account exists: { email, emailSent }
  const [registered, setRegistered] = useState(null);
  const [resendWait, startResendWait] = useCooldown();

  const handleSignup = async (e) => {
    e.preventDefault();
    setError("");

    try {
      if (!isValidEmail(email.trim())) throw authError("auth/invalid-email");
      const normalizedPhone = normalizePhone(phone);
      if (!normalizedPhone) throw authError("fqst/invalid-phone");
      if (password.length < MIN_PASSWORD) throw authError("auth/weak-password");
      if (password !== confirmPassword) throw authError("fqst/password-mismatch");

      setSubmitting(true);
      const result = await registerUser(
        email,
        password,
        `${firstName.trim()} ${lastName.trim()}`,
        normalizedPhone
      );
      setRegistered(result);
      // The first email just went out: pace the resend button like Firebase does
      if (result.emailSent) startResendWait(RESEND_DELAY);
    } catch (err) {
      reportAuthError(err);
      setError(authErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleResend = async () => {
    setError("");
    setMessage("");
    try {
      const { alreadyVerified } = await resendVerificationEmail(email, password);
      setMessage(
        alreadyVerified
          ? "Votre adresse est déjà confirmée : vous pouvez vous connecter."
          : `Courriel renvoyé à ${registered.email}.`
      );
      if (!alreadyVerified) startResendWait(RESEND_DELAY);
    } catch (err) {
      reportAuthError(err);
      setError(authErrorMessage(err));
      if (err?.code === "auth/too-many-requests") startResendWait(RESEND_DELAY);
    }
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gray-800 w-full px-4 py-8">
      <AuthHeader />

      {/* Animated Success Message or Signup Form */}
      <motion.div
        className="w-full max-w-md p-6 bg-white rounded-lg shadow-xl mb-16"
        initial={{ opacity: 0, y: 50 }} // Start slightly below
        animate={{ opacity: 1, y: 0 }} // Fade in and move up
        transition={{ duration: 0.8, ease: "easeOut" }} // Smooth transition
      >
        {registered ? (
          <div className="text-center text-gray-700">
            <h2 className="text-2xl font-semibold text-green-600 mb-4">
              Compte créé !
            </h2>
            {registered.emailSent ? (
              <p className="mb-3">
                Pour activer votre compte, cliquez sur le lien envoyé à{" "}
                <strong>{registered.email}</strong>, puis connectez-vous.
              </p>
            ) : (
              <p className="mb-3 text-amber-700">
                Votre compte est créé, mais le courriel de confirmation n'a pas
                pu être envoyé à <strong>{registered.email}</strong>. Cliquez
                sur « Renvoyer le courriel » ci-dessous.
              </p>
            )}
            <p className="mb-4 text-sm text-gray-500">
              Pas reçu ? Il peut prendre quelques minutes à arriver. Vérifiez
              vos courriels indésirables (spam) et l'onglet Promotions. Si
              l'adresse ci-dessus contient une erreur, inscrivez-vous de nouveau
              avec la bonne adresse.
            </p>

            {error && (
              <p role="alert" className="text-red-500 text-sm mb-3">
                {error}
              </p>
            )}
            {message && (
              <p role="status" className="text-green-600 text-sm mb-3">
                {message}
              </p>
            )}

            <div className="flex flex-col gap-3 items-center">
              <button
                type="button"
                onClick={handleResend}
                disabled={resendWait > 0}
                className="text-blue-500 hover:underline text-sm disabled:text-gray-400 disabled:no-underline"
              >
                {resendWait > 0
                  ? `Renvoyer le courriel (${resendWait} s)`
                  : "Renvoyer le courriel"}
              </button>
              <Link
                to="/login"
                className="py-2 px-6 text-lg text-center font-semibold bg-green-700 hover:bg-green-600 text-white rounded-lg shadow-xl transition"
              >
                Se connecter
              </Link>
            </div>
          </div>
        ) : (
          <>
            <h2 className="text-2xl font-semibold text-center text-gray-700 mb-6">
              Inscription
            </h2>

            {error && (
              <p role="alert" className="text-red-500 text-sm text-center mb-4">
                {error}
              </p>
            )}

            <form onSubmit={handleSignup} className="space-y-4">
              <input
                type="text"
                placeholder="Prénom"
                autoComplete="given-name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                required
                className={inputClass(false)}
              />
              <input
                type="text"
                placeholder="Nom"
                autoComplete="family-name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                required
                className={inputClass(false)}
              />
              <input
                type="email"
                placeholder="Courriel"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className={inputClass(email && !isValidEmail(email.trim()))}
              />
              <input
                type="tel"
                placeholder="Téléphone (ex. 514 555-1234)"
                autoComplete="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                required
                className={inputClass(phone && !normalizePhone(phone))}
              />
              <input
                type="password"
                placeholder={`Mot de passe (${MIN_PASSWORD} caractères minimum)`}
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className={inputClass(password && password.length < MIN_PASSWORD)}
              />
              <input
                type="password"
                placeholder="Confirmer le mot de passe"
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                className={inputClass(confirmPassword && password !== confirmPassword)}
              />
              <button
                type="submit"
                disabled={submitting}
                className="w-full p-2 text-white bg-blue-500 rounded-lg hover:bg-blue-600 transition flex items-center justify-center disabled:opacity-60"
              >
                {submitting ? (
                  <div className="h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  "Créer mon compte"
                )}
              </button>
            </form>

            <p className="mt-4 text-center text-sm text-gray-600">
              Déjà un compte ?{" "}
              <Link to="/login" className="text-blue-500 hover:underline">
                Connectez-vous
              </Link>
            </p>
          </>
        )}
      </motion.div>
    </div>
  );
};

export default Signup;
